import { skipToken } from '@reduxjs/toolkit/query';
import PinButton from 'components/buttons/PinButton';
import Loader from 'components/Loader.tsx';
import PageHeader from 'components/PageHeader.tsx';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout.tsx';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useGetBankTransferByIdQuery } from 'store/api/bank-transfers.ts';
import { BankTransactionDetails } from 'types/bank-transfers.ts';
import { getClassifierText } from 'utils/getTranslations.ts';
import { recordPin } from 'utils/pins';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { useGoBack } from 'utils/useNavigateWithReturnState.ts';
import BankTransferBalance from './BankTransferBalance.tsx';
import BankTransferInformation from './BankTransferInformation.tsx';
import BankTransferStatusIcon from './BankTransferStatusIcon.tsx';

const BankTransferDetails = () => {
    const { id } = useParams();
    const goBack = useGoBack('/bank-transfers');
    const isNew = useIsNewDesign();
    const { data, isLoading } = useGetBankTransferByIdQuery(id ?? skipToken);
    const [bankTransfer, setBankTransfer] = useState<BankTransactionDetails | undefined>(data);

    // TODO(react-doctor:no-derived-state-effect): bankTransfer is a controlled local copy of the
    // RTK query result (children push optimistic updates via onBankTransferUpdated). Replacing
    // this effect requires either invalidating the query tag in the children or merging the
    // override pattern carefully so a refetch wins over a stale override; leaving as-is for now.
    useEffect(() => {
        setBankTransfer(data);
    }, [data]);

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['BANK_TRANSACTION_STATUS_TYPE'],
    });

    const classifierText = getClassifierText(
        classifiers['BANK_TRANSACTION_STATUS_TYPE'],
        bankTransfer?.bankTransactionStatusTypeCode
    );

    if (isLoading || isLoadingClassifiers) {
        return <Loader />;
    }

    if (!bankTransfer) {
        return null;
    }

    return (
        <PaddedLayout>
            <PageHeader
                title={bankTransfer.entryTransactionId}
                showBreadcrumb
                onBack={goBack}
                iconsEnd={
                    isNew ? (
                        <PinButton
                            pin={recordPin('bank-transfers', bankTransfer.id, bankTransfer.entryTransactionId)}
                        />
                    ) : undefined
                }
            />
            <div className="flex h-full flex-col gap-4">
                <div className="flex flex-row items-center gap-2 pt-2">
                    <BankTransferStatusIcon
                        bankTransactionStatusTypeCode={bankTransfer.bankTransactionStatusTypeCode}
                        isWithTooltip={false}
                    />
                    <p className="text-body1 font-semibold">{classifierText}</p>
                </div>
                <div className="new:gap-(--card-gap) flex flex-row gap-8">
                    <div className="flex-1">
                        <BankTransferInformation bankTransfer={bankTransfer} />
                    </div>
                    <div className="flex-1">
                        <BankTransferBalance bankTransfer={bankTransfer} onBankTransferUpdated={setBankTransfer} />
                    </div>
                </div>
            </div>
        </PaddedLayout>
    );
};

export default BankTransferDetails;
